import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Sparkles } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const TYPES = [
  { key: "split", labelKey: "nav.split" },
  { key: "cassette", labelKey: "nav.cassette" },
  { key: "floor", labelKey: "nav.floor" },
  { key: "duct", labelKey: "nav.duct" },
];

const SORTS = [
  { key: "featured", labelKey: "shop.sort.featured" },
  { key: "price-asc", labelKey: "shop.sort.priceAsc" },
  { key: "price-desc", labelKey: "shop.sort.priceDesc" },
  { key: "name", labelKey: "shop.sort.name" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [ratings, setRatings] = useState({});
  const { t } = useLang();

  const brand = searchParams.get("brand") || "";
  const type = searchParams.get("type") || "";
  const onSale = searchParams.get("on_sale") === "true";
  const sort = searchParams.get("sort") || "featured";
  const q = searchParams.get("q") || "";

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/ratings").then((r) => setRatings(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (brand) params.brand = brand;
    if (type) params.type = type;
    if (onSale) params.on_sale = true;
    if (q) params.q = q;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [brand, type, onSale, q]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const sortedProducts = useMemo(() => {
    const arr = [...products];
    switch (sort) {
      case "price-asc":
        arr.sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price));
        break;
      case "price-desc":
        arr.sort((a, b) => (b.sale_price || b.price) - (a.sale_price || a.price));
        break;
      case "name":
        arr.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        arr.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return arr;
  }, [products, sort]);

  const activeFilters = [
    brand && { key: "brand", label: brand },
    type && { key: "type", label: type.toUpperCase() },
    onSale && { key: "on_sale", label: t("shop.onSale") },
    q && { key: "q", label: `"${q}"` },
  ].filter(Boolean);

  return (
    <div data-testid="shop-page" className="bg-white">
      {/* Hero strip */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F0F4F8] via-white to-[#E0F2FE]">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-electric">
                <Sparkles size={14} /> {t("shop.eyebrow")}
              </div>
              <h1 className="mt-3 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
                {t("shop.heroTitle1")}<br />{t("shop.heroTitle2")}
              </h1>
              <p className="mt-4 max-w-md text-slate-600 text-base">
                Comfee · General · Viua · Milon
              </p>
            </div>

            {/* Search bar — borderless soft pill */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateParam("q", searchTerm);
              }}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-md rounded-full pl-5 pr-2 h-14 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.15)] w-full sm:w-[420px]"
            >
              <Search size={18} className="text-slate-400" />
              <input
                data-testid="shop-search-input"
                type="text"
                placeholder={t("shop.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 h-full bg-transparent focus:outline-none text-sm placeholder:text-slate-400"
              />
              <button
                type="submit"
                className="h-10 px-5 rounded-full bg-ink text-white text-xs font-mono uppercase tracking-widest hover:bg-electric transition-colors"
              >
                {t("common.search")}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Filter bar — floating pills, no hard borders */}
      <section className="sticky top-16 z-20 bg-white/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center gap-3 overflow-x-auto">
          <div className="flex items-center gap-1.5 mr-2 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 shrink-0">
            <SlidersHorizontal size={12} /> {t("shop.filter")}
          </div>

          <Pill
            active={!type}
            onClick={() => updateParam("type", "")}
            testid="filter-type-all"
            label={t("shop.all")}
          />
          {TYPES.map((tp) => (
            <Pill
              key={tp.key}
              active={type === tp.key}
              onClick={() => updateParam("type", tp.key)}
              testid={`filter-type-${tp.key}`}
              label={t(tp.labelKey)}
            />
          ))}

          <span className="w-px h-6 bg-black/10 mx-2 shrink-0" />

          {brands.map((b) => (
            <Pill
              key={b}
              active={brand === b}
              onClick={() => updateParam("brand", brand === b ? "" : b)}
              testid={`filter-brand-${b}`}
              label={b}
              tone="brand"
            />
          ))}

          <span className="w-px h-6 bg-black/10 mx-2 shrink-0" />

          <Pill
            active={onSale}
            onClick={() => updateParam("on_sale", onSale ? "" : "true")}
            testid="filter-on-sale"
            label={t("shop.onSale")}
            tone="accent"
          />

          <div className="ml-auto flex items-center gap-3 shrink-0">
            <select
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => updateParam("sort", e.target.value)}
              className="h-10 pl-4 pr-9 rounded-full bg-secondary text-sm font-mono focus:outline-none focus:ring-2 focus:ring-electric/30 cursor-pointer appearance-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23111' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
              }}
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {t(s.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-4 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{t("shop.active")}</span>
            {activeFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  if (f.key === "q") setSearchTerm("");
                  updateParam(f.key, "");
                }}
                className="inline-flex items-center gap-1.5 h-7 pl-3 pr-2 rounded-full bg-ink text-white text-[11px] font-mono uppercase tracking-wider hover:bg-electric transition-colors"
              >
                {f.label}
                <X size={11} />
              </button>
            ))}
            <button
              onClick={() => setSearchParams(new URLSearchParams())}
              className="ml-1 text-[11px] font-mono uppercase tracking-wider text-slate-500 hover:text-electric"
            >
              {t("shop.clearAll")}
            </button>
          </div>
        )}
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[4/5] rounded-2xl bg-white animate-pulse" />
                <div className="h-4 w-2/3 bg-secondary animate-pulse rounded-full" />
                <div className="h-3 w-1/3 bg-secondary animate-pulse rounded-full" />
              </div>
            ))}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="py-24 text-center">
            <div className="inline-flex w-16 h-16 rounded-full bg-secondary items-center justify-center mb-6">
              <Search size={24} className="text-slate-400" />
            </div>
            <h3 className="font-display font-extrabold text-3xl">{t("shop.empty")}</h3>
            <p className="mt-2 text-slate-500">{t("shop.emptyHint")}</p>
            <button
              onClick={() => setSearchParams(new URLSearchParams())}
              className="mt-6 inline-flex items-center h-11 px-6 rounded-full bg-ink text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-electric transition-colors"
            >
              {t("shop.resetFilters")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {sortedProducts.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} rating={ratings[p.slug]} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Pill({ active, onClick, label, testid, tone = "default" }) {
  const base = "shrink-0 h-9 px-4 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] transition-all duration-300";
  let cls;
  if (active) {
    if (tone === "accent") cls = `${base} bg-electric text-white shadow-[0_4px_20px_-4px_rgba(0,85,255,0.5)]`;
    else cls = `${base} bg-ink text-white`;
  } else {
    cls = `${base} bg-secondary text-slate-700 hover:bg-ink hover:text-white`;
  }
  return (
    <button onClick={onClick} data-testid={testid} className={cls}>
      {label}
    </button>
  );
}
